import type {ChildMenuBaseAttrs} from 'common/query-builder/query-block/child-menu';
import type {NoExtension, Section, SectionItem, SelectAllMechanism} from 'common/components/items-menu/types';
import {PropertyType, ResourceType} from 'common/report/constants';
import type {SpecialTimeUnit, TimeUnit} from 'common/widgets/date-range-picker/models/types';
import type {Cohort} from 'common/util/definitions-types';
import {ChangeEvent} from 'common/components/select/types';
import {DataGroup} from 'common/widgets/data-group-selector/types';
import {Behavior, CustomProperty} from 'common/types/reports/query';

import {InlineCohortSegment} from '../segment-menu/types';
import {EventSectionItem, PropertySectionItem} from './item-menu-types';

/**
 * The "value" of a property is usually a string (representing the property name)
 * but can also be an array of strings in the case of a lookup table property.
 * When it's a lookup table, the last value is the lookup property name and the
 * preceding values are the path (usually just the join key)
 * TODO: This eventually be something more strict like the following:
 * type RawPropertyValue = string;
 * type LookupPropertyValue = [string, string];
 * type PropertyValue = RawPropertyValue | LookupPropertyValue[];
 */
type PropertyValue = string | string[];

/**
 * Defines all the different property types that this dropdown can render
 *
 * aims to replace the convoluted way of defining what this menu renders by
 * using attributes like `project-has-people`, `project-has-cohorts`, etc
 */
export enum MenuItemType {
  Events = `events`,
  EventCustomProperties = `event_custom_properties`,
  UserCustomProperties = `user_custom_properties`,
  LookupTableProperties = `lookup_table_properties`,
  EventProperties = `event_properties`,
  UserProperties = `user_properties`,
  Cohorts = `cohorts`,
  DataGroupProperties = `datagroup_properties`,
  Other = `other`,
}

export type Property = {
  propertyDefaultType: PropertyType;
  resourceType: ResourceType;
  type: PropertyType;
  value: PropertyValue;
  label?: string; // this seems to only be used for special (like $all_people) or "other" properties (like $paths)
  customPropertyId?: string | number;
  customProperty?: CustomProperty;
  joinPropertyType?: PropertyType;
  tempDataGroupId?: string;
  dataGroupId?: string;
  propertyObjectKey?: string | string[];

  /** represents a computed property based on each user's event stream */
  behavior?: Behavior;

  /** Used to specify a unit within a date property. For example, when segmenting on a date */
  unit?: TimeUnit | SpecialTimeUnit;
};

export type InlineCohort = {
  resourceType: ResourceType.Cohort;
  value: InlineCohortSegment;
  label: string;
};

export type SavedCohort = {
  resourceType: ResourceType.Cohort;
  value: number;
  label: string;
  dataGroupId?: string;
};

export type DataGroupItem = DataGroup & {
  properties: any[];
  isLoading: boolean;
};

/**
 * These represents the serializable form of a PropertyItem.
 * This list should not contain any UI properties as is saved to the bookmark
 * Any properties should also be included in SerializedItemKeyMap
 */
export type Item = Property | SavedCohort | InlineCohort;

export function isCohort(item: Item): item is InlineCohort | SavedCohort {
  return item && item.resourceType === ResourceType.Cohort;
}

export function isSavedCohort(item: Item): item is SavedCohort {
  return isCohort(item) && typeof item.value === `number`;
}

export function isInlineCohort(item: Item): item is InlineCohort {
  return isCohort(item) && typeof item.value !== `number` && !!item.value.cohort;
}

export function isProperty(item: Item): item is Property {
  return item && item.resourceType !== ResourceType.Cohort;
}

export function isEventSectionItem(item: EventSectionItem | PropertySectionItem): item is EventSectionItem {
  return item && item.menuItemType === MenuItemType.Events;
}

export function isPropertySectionItem(item: EventSectionItem | PropertySectionItem): item is PropertySectionItem {
  return item && item.menuItemType !== MenuItemType.Events;
}

export type Selection = Item | Item[];

type ItemIntersection = Property & SavedCohort & InlineCohort;

/**
 * This boolean map is used to filter any property item to only the keys in Item
 */
export const SerializedItemKeyMap: {
  [P in keyof Required<ItemIntersection>]: true;
} = {
  customPropertyId: true,
  behavior: true,
  customProperty: true,
  dataGroupId: true,
  joinPropertyType: true,
  propertyDefaultType: true,
  propertyObjectKey: true,
  resourceType: true,
  tempDataGroupId: true,
  type: true,
  unit: true,
  value: true,
  label: true,
};

export const NonDataGroupPropertyItemIdentifierKeys: Array<keyof ItemIntersection> = [`customPropertyId`, `value`];

// Checked in order
export const PropertyItemPersistedIdentifierKeys: Array<keyof ItemIntersection> = [
  `tempDataGroupId`,
  `dataGroupId`,
  ...NonDataGroupPropertyItemIdentifierKeys,
];

export enum SpecialActions {
  CreateInlineCohort = `create_inline_cohort`,
  CreateCustomEventFormula = `create_custom_event_formula`,
  CreateCustomPeopleFormula = `create_custom_people_formula`,
  CreateCustomBehavioralProperties = `create_custom_behavioral_property`,
}

export type SpecialControl = {
  icon: string;
  isControlItem: boolean;
  propertyDefaultType: PropertyType;
  type: PropertyType;
  label: string;
  resourceType: ResourceType;
  value: SpecialActions;
  tooltipData?: {
    header: string;
    description: string;
    entityIcon: string;
    helpLink?: {
      label: string;
      href: string;
    };
  };
};

export enum PropertyItemAction {
  Typecast = `typecast`,
}

export enum PropertyCombineMode {
  Union = `union`,
  Intersection = `intersection`,
}

export type CohortItem = Cohort & {
  label: string;
  canView?: boolean;
  canUpdateBasic?: boolean;
  canShare?: boolean;
  isSuperadmin?: boolean;
};

export type GetSortedMenuSections = (sections: Section[]) => Section[];

export enum StagedItemType {
  Typecast,
  Dimension,
  Datetime,
  Object,
  User,
  Group,
  Cohort,
  BrowseProperties,
}

export const AllPropertiesTab = `all_properties`;
export const AllTab = `all`;
export const EventsInPropertiesMenuTab = `events`;
/**
 * selectedTab can be `all`, a dataGroupId or a resourceType
 */
export type SelectedTab =
  | typeof AllTab
  | typeof AllPropertiesTab
  | typeof EventsInPropertiesMenuTab
  | string
  | ResourceType;

export enum IncludeCustomPropertyType {
  All = `all`,
  None = `none`,
  PersistedOnly = `persisted-only`,
  PersistedNonBehavioralOnly = `persisted-non-behavioral-only`,
}

export enum TypecastPillAction {
  /**
   * @deprecated Typecasting should be selected through the overflow menu. This value is reserved for uses that do not currently have an overflow menu.
   */
  Typecast = `typecast`,
  TriggerTutorial = `trigger-tutorial`,
}
export interface PropertiesMenuAttrs {
  'cohort-multiselect'?: boolean;
  'cohort-operation-type'?: string;
  'apply-button'?: string;
  'apply-button-disabled'?: boolean;
  'allow-behavioral-property-creation'?: boolean;
  'allow-typecast-pill-action'?: TypecastPillAction;
  'enable-workspace-filter-restrictions'?: boolean;
  'disabled-items'?: Obj;
  'include-custom-properties'?: IncludeCustomPropertyType;
  'exclude-dimension-properties'?: boolean;
  'exclude-lookup-table-properties'?: boolean;
  'property-combine-mode'?: PropertyCombineMode;
  'show-all-tab'?: boolean;
  'show-all-users-cohort'?: boolean;
  'show-items-caret'?: boolean;
  'single-data-group'?: string;
  'other-options'?: string[];
  'select-all-mechanism'?: SelectAllMechanism;
  'show-select-all'?: boolean;
  'select-all-disabled'?: boolean;
  'hide-recently-viewed'?: boolean;
  'hide-section-labels'?: boolean;
  'selectable-sensitive-properties'?: boolean;
  'description-edit-disabled'?: boolean;
  'menu-item-types'?: MenuItemType[];
  'no-rounded-corners'?: boolean;
}

// export type SelectedPropertyItem<ItemT = PropertyItemSectionItem | EventItem> = {
export type SelectedPropertyItem = {
  item: Selection;
  rawItem: PropertySectionItem;
  autoTriggered: boolean;
  commit?: boolean;
  menuItemType?: MenuItemType;
  onItemClicked?: boolean;
};

export type SelectPropertyEvent = CustomEvent<SelectedPropertyItem>;

export type PropertiesMenuJsxProps = {
  attrs: ChildMenuBaseAttrs<Selection> & PropertiesMenuAttrs;
  on?: {
    select?: (ev: SelectPropertyEvent) => void;
  };
  props?: {
    /** For composite menu flows (e.g. selecting an event + property) we allow external control for the back button on this menu */
    backButtonHandler?: () => void;
    /** Defines the type of sub menu a property is being picked on (e.g. filter or breakdown) */
    context?: string;
    /** Takes over keyboard events on the mp-items-menu in this menu */
    keydownProxyEl?: HTMLElement;
  };
};

export enum SortBy {
  Relevance = `relevance`,
  NameAsc = `name-asc`,
  NameDesc = `name-desc`,
}

export type SortSectionItem = SectionItem<SortBy, NoExtension>;
export type SortMenuChangeEvent = ChangeEvent<SortBy, NoExtension>;

export type FilterValue = {
  value: string;
  resourceType?: ResourceType | `events`;
  dataGroupId?: string;
};

export type FilterSectionItem = SectionItem<FilterValue, NoExtension>;
export type FilterMenuChangeEvent = ChangeEvent<FilterValue, NoExtension>;

// FunnelsFilter maps to both Funnels and Retention reports with a key at the primary layer (event / user)
export type FunnelsFilter = {
  event?: {
    filterOperator?: string;
    filterValue?: any;
    propertyDefaultType?: string;
    propertyName?: string | [string, string, ...string[]];
    propertyObjectKey?: string | null;
    propertyType?: string;
    resourceType?: string;
  };
  user?: {
    filterOperator?: string;
    filterValue?: any;
    propertyDefaultType?: string;
    propertyName?: string | [string, string, ...string[]];
    propertyObjectKey?: string | null;
    propertyType?: string;
    resourceType?: string;
  };
};

export type FunnelsGroup = {
  event?: {
    propertyDefaultType?: string;
    propertyName?: string | [string, string, ...string[]];
    propertyType?: string;
    resourceType?: string;
  };
  user?: {
    propertyDefaultType?: string;
    propertyName?: string | [string, string, ...string[]];
    propertyType?: string;
    resourceType?: string;
  };
};

// InsightsFilter maps to the Insights Report and does not have a key at the primary layer
export type InsightsFilter = {
  dataGroupId?: string | null;
  dataset?: string | null;
  defaultType?: string;
  filterOperator?: string;
  filterType?: string;
  filterValue?: any;
  profileType?: string | null;
  propertyObjectKey?: string | null;
  resourceType?: string;
  search?: string;
  value?: any;
};

export type InsightsGroup = {
  dataGroupId?: string | null;
  dataset?: string | null;
  filterOperator?: null;
  filterType?: string;
  filterValue?: null;
  profileType?: string | null;
  propertyType?: string;
  resourceType?: string;
  search?: string;
  typeCast?: string | null;
  unit?: string | null;
  value?: any;
};
